import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

// HOOKS
import useScreenSize from "../../hooks/useScreenSize";
import { useModalBackGesture } from "../../hooks/useModalBackGesture";
import { useGtmEvent } from "../../hooks/useGtmEvent";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH, MEP_SENEBI_THRESHOLD } from "./constants";

// UTILS
import { useFormik } from "formik";
import { operateSchema } from "./utils/validations";

// REDUX
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  closeOperationMEPModal,
  updateOperationMEPData,
  updateOperationMEPModal,
} from "../../store/slices/operationMEPSlice";

// CONNECTOR
import {
  getMEPQuote,
  getSymbolsPositions,
} from "../../connectors/operationApi";

// TYPES
import { OperateFormValues } from "./types";

// UI
import { ModalContent } from "@nextui-org/react";

// COMPONENTS
import Spinner from "../Spinner/Spinner";
import CustomModal from "../CustomModal/CustomModal";
import OperateSelectorMEP from "./OperateSelector";
import CancelOperationModal from "../../pages/MarketPage/ui/OperateModal/ui/CancelOperationModal/CancelOperationModal";
import ToastMessage from "../ToastMessage/ToastMessage";

//STYLES
import styles from "./OperateModal.module.scss";

const OperateModal = React.memo(({}) => {
  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);
  const dispatch = useAppDispatch();
  const { pushEvent } = useGtmEvent();
  const {
    openModal,
    data: operationData,
    operationState,
  } = useAppSelector((state) => state.operationMEPData);
  const { accounts } = useAppSelector((state) => state.userAccounts);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);

  const accountOptions = useMemo(
    () =>
      accounts?.map((acc) => ({
        value: acc.number,
        label: `${acc.name} - ${acc.number}`,
      })) ?? [],
    [accounts],
  );

  const {
    content,
    toastContainer,
    toast,
    spinnerContainer,
    contentSpinner,
    closeButton,
  } = styles;

  // -------- Formik -------- //

  const initialValues: OperateFormValues = useMemo(
    () => ({
      account: "",
      side: "BUY",
      isTotal: false,
      operationValueArs: "",
      operationValueMep: "",
      acceptedTermsAndConditions: false,
      currencySelected: "ARS",
      isResident: true,
    }),
    [],
  );

  const onSubmit = () => {
    // Map form values to the Redux data structure
    const operationData = {
      isDetailsView: false,
      account: values.account,
      side: values.side,
      isTotal: values.isTotal,
      operationValueArs: values.operationValueArs,
      operationValueMep: values.operationValueMep,
      isResident: values.isResident,
    };

    // Save data to Redux and transition to REVIEW state
    dispatch(updateOperationMEPData(operationData));
    dispatch(updateOperationMEPModal("REVIEW"));
  };

  const {
    values,
    errors,
    setFieldValue,
    isValid,
    handleSubmit,
    resetForm,
    setValues,
  } = useFormik({
    initialValues,
    validationSchema: operateSchema,
    validate: (values) => {
      const errors: Partial<Record<keyof OperateFormValues, string>> = {};
      const minimumMep = 1;
      const maxMep = 50_000;

      // Helper function to parse numeric value
      const parseNumericValue = (value: string): number => {
        return Number(value);
      };

      // ARS validation
      if (values.operationValueArs && values.operationValueArs.trim() !== "") {
        const arsNumericValue = parseNumericValue(values.operationValueArs);

        if (values.side === "BUY" && arsNumericValue > possessionAvailable) {
          errors.operationValueArs =
            "El monto debe ser menor o igual a tu disponible.";
        }
      }

      // MEP validations
      if (values.operationValueMep && values.operationValueMep.trim() !== "") {
        const mepNumericValue = parseNumericValue(values.operationValueMep);
        if (mepNumericValue < minimumMep) {
          errors.operationValueMep = "El monto mínimo es 1 dólar MEP.";
        }

        if (values.side === "BUY" && mepNumericValue > maxMep) {
          errors.operationValueMep = "Podés comprar hasta 50.000 dólares MEP.";
        }

        if (values.side === "SELL" && mepNumericValue > possessionAvailable) {
          errors.operationValueMep =
            "El monto debe ser menor o igual a tu disponible.";
        }
      }
      return errors;
    },

    onSubmit: onSubmit,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnMount: true,
  });

  //Posessions availables
  const {
    data: symbolPositionData,
    isLoading: symbolPositionLoading,
    refetch: refetchPositions,
  } = useQuery({
    queryFn: () => getSymbolsPositions({ accountId: values.account }),
    queryKey: ["symbol-position", values.account],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000,
    enabled: openModal && !!values.account,
  });

  // OMS Positions
  const possessionAvailable =
    values.side === "BUY"
      ? symbolPositionData?.positions_by_settlement?.T0?.ARS?.available || 0
      : symbolPositionData?.positions_by_settlement?.T0?.USD?.available || 0;

  // -------- Close modal -------- //

  const handleClose = () => {
    if (operationState !== "SUMMARY") {
      pushEvent("abandoned_operation", {
        step: operationState,
        symbol_type: "DOLLAR_MEP",
        side: values.side,
      });
    }
    dispatch(closeOperationMEPModal());
    resetForm();
    setShowCancelConfirmation(false);
  };

  const handleCancelOperation = () => {
    if (!values.operationValueArs && !values.operationValueMep) {
      handleClose();
      return;
    }
    setShowCancelConfirmation(true);
  };

  const handleBackGesture = () => {
    if (showCancelConfirmation) {
      setShowCancelConfirmation(false);
      return;
    }
    // From REVIEW step, step back to the DRAFT form instead of closing
    if (operationState === "REVIEW") {
      dispatch(updateOperationMEPModal("DRAFT"));
      return;
    }
    handleCancelOperation();
  };

  useModalBackGesture({
    isOpen: openModal,
    onBack: handleBackGesture,
  });

  // -------- Mep price -------- //

  const { data: mepQuotes, isLoading: isLoadingMepQuotes } = useQuery({
    queryFn: () => getMEPQuote({ accountId: accountOptions?.[0]?.value }),
    queryKey: ["mepPrice"],
    enabled:
      !!accountOptions?.[0] && openModal && !operationData?.isDetailsView,
    refetchOnMount: "always",
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: 0,
    gcTime: 0,
  });

  const mepPrice =
    Number(values.operationValueMep) < MEP_SENEBI_THRESHOLD
      ? mepQuotes?.PPT
      : mepQuotes?.SENEBI;

  const mepPriceToShow: string | number =
    values.side === "BUY"
      ? mepQuotes?.SENEBI?.BUY || "-"
      : mepQuotes?.SENEBI?.SELL || "-";

  // -------- Accounts -------- //

  const handleAccountChange = useCallback(
    (value: string) => {
      setFieldValue("account", value);
    },
    [setFieldValue],
  );

  // Autoselect account if only one option available
  useEffect(() => {
    if (
      (accountOptions?.length === 1 && !values?.account) ||
      values.account === ""
    ) {
      setFieldValue("account", accountOptions?.[0]?.value);
    }
  }, [accountOptions, setFieldValue, values?.account]);

  useEffect(() => {
    if (openModal) {
      pushEvent("started_operation", { symbol_type: "DOLLAR_MEP" });
    }
  }, [openModal]);

  const handleNonInteractiveClick = (e: React.MouseEvent) => {
    if (operationState !== "DRAFT" && operationState !== "REVIEW") return;
    const target = e.target as HTMLElement;
    if (
      !target.closest(
        "button, input, a, select, textarea, label, [role='button']",
      )
    ) {
      pushEvent("failed_operation", {
        step: operationState,
        symbol_type: "DOLLAR_MEP",
      });
    }
  };

  // Minimize the modal when SummaryModal has its own CustomModal
  const shouldMinimizeModal = operationState === "SUMMARY";

  if (!openModal) return null;

  return (
    <>
      <CustomModal
        isOpen={openModal}
        onClose={handleCancelOperation}
        isDismissable={true}
        isKeyboardDismissDisabled
        disableNativeKeyboardHandling
        classNames={{
          ...(isMobile
            ? { body: "px-4 pb-4 py-0", base: "w-full h-full max-w-full m-0" }
            : { body: "m-0 p-0 gap-0" }),
          closeButton: closeButton,
          ...(shouldMinimizeModal && !isMobile
            ? {
                base: "min-w-0 max-w-0 w-0 h-0 m-0 p-0 overflow-hidden",
                wrapper: "pointer-events-none",
              }
            : {}),
        }}
        hideCloseButton={isMobile}
        style={{
          marginRight: "0 !important",
          ...(shouldMinimizeModal && !isMobile
            ? {
                width: "0px",
                height: "0px",
                minWidth: "0px",
                maxWidth: "0px",
              }
            : {}),
        }}
      >
        <ToastMessage messageId="operateMEPModal" />
        <ModalContent
          className={`${!isLoadingMepQuotes ? content : contentSpinner}`}
          style={
            shouldMinimizeModal && !isMobile
              ? {
                  width: "0px",
                  height: "0px",
                  minWidth: "0px",
                  maxWidth: "0px",
                  padding: "0px",
                  margin: "0px",
                  overflow: "hidden",
                }
              : {}
          }
        >
          <ToastMessage
            messageId="operateMEPModal"
            className={toastContainer}
            toastClassName={toast}
            position="top-center"
          />
          <div
            onClick={handleNonInteractiveClick}
            style={{ display: "contents" }}
          >
            {isLoadingMepQuotes && (
              <div className={spinnerContainer}>
                <Spinner />
              </div>
            )}
            {!isLoadingMepQuotes && (
              <OperateSelectorMEP
                values={values}
                setFieldValue={setFieldValue}
                mepPrice={mepPrice}
                errors={errors}
                isValid={isValid}
                availableArs={possessionAvailable}
                availableMep={possessionAvailable}
                handleSubmit={handleSubmit}
                handleClose={handleCancelOperation}
                accountOptions={accountOptions}
                handleAccountChange={handleAccountChange}
                resetForm={resetForm}
                setValues={setValues}
                isMobile={isMobile}
                refetchPositions={refetchPositions}
                symbolPositionLoading={symbolPositionLoading}
                mepPriceToShow={mepPriceToShow}
              />
            )}
          </div>
        </ModalContent>
      </CustomModal>

      {showCancelConfirmation && (
        <CancelOperationModal
          handleClose={handleClose}
          handleGoBack={() => setShowCancelConfirmation(false)}
        />
      )}
    </>
  );
});

export default OperateModal;
